import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import { getInstitutionShortLabel } from '../../utils/institutionAssets';
import { getProgrammePrimaryTitle, getProgrammeSecondaryTitle } from '../../utils/programmeDisplay';
import {
    CLASSIFICATION_BADGE_TEXT_CLASS,
    CLASSIFICATION_BADGE_WIDTH_CLASS,
    getClassificationBadgeDisplay,
    getClassificationLoadingBadgeDisplay,
    isClassificationUnavailable,
    JUPAS_HIGH_RISK_ACTION_CLASS,
    JUPAS_VERY_SAFE_ACTION_CLASS,
    REQUIREMENT_BADGE_SIZE_CLASS,
    isRequirementFulfillmentAwaiting,
    resolveRequirementBadgeValue,
    resolveRequirementFulfillmentDisplay,
} from '../../utils/jupasClassificationDisplay';
import {
  JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS,
  JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS,
  JUPAS_PROGRAMME_BAR_IDENTITY_CLASS,
  JUPAS_PROGRAMME_BAR_MAIN_CLASS,
  JUPAS_PROGRAMME_BAR_ROW_CLASS,
  JUPAS_PROGRAMME_BAR_TITLE_CLASS,
  JUPAS_PROGRAMME_BAR_TRAILING_CLASS,
  JUPAS_PROGRAMME_CODE_LABEL_CLASS,
  JUPAS_RECOMMENDATION_VISIBLE_ROWS,
  JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS,
} from '../../utils/jupasLayoutDisplay';
import JupasProgrammeCard from './JupasProgrammeCard';
import CollapsibleSlide from '../ui/CollapsibleSlide';
import MobileProgrammeSwipeAction from './MobileProgrammeSwipeAction';

const buildPageItems = (currentPage, totalPages, maxVisibleNumbers = 7) => {
    if (totalPages <= maxVisibleNumbers) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const innerSlots = Math.max(1, maxVisibleNumbers - 2);
    let start = currentPage - Math.floor(innerSlots / 2);
    let end = start + innerSlots - 1;
    if (start < 2) {
        start = 2;
        end = start + innerSlots - 1;
    }
    if (end > totalPages - 1) {
        end = totalPages - 1;
        start = end - innerSlots + 1;
    }

    const sorted = [
        1,
        ...Array.from({ length: end - start + 1 }, (_, index) => start + index),
        totalPages,
    ];
    const items = [];
    sorted.forEach((page, index) => {
        if (index > 0 && page - sorted[index - 1] > 1) {
            items.push(`ellipsis-${sorted[index - 1]}`);
        }
        items.push(page);
    });
    return items;
};

const buildPageItemsToFit = (currentPage, totalPages, maxItems) => {
    let best = buildPageItems(currentPage, totalPages, Math.min(5, totalPages));
    for (let visibleNumbers = 6; visibleNumbers <= totalPages; visibleNumbers += 1) {
        const candidate = buildPageItems(currentPage, totalPages, visibleNumbers);
        if (candidate.length > maxItems) break;
        best = candidate;
    }
    return best;
};

const buildMobilePageItems = (currentPage, totalPages) => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 3) {
        return [1, 2, 3, 4, 'ellipsis-end', totalPages];
    }
    if (currentPage >= totalPages - 2) {
        return [1, 'ellipsis-start', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [
        1,
        'ellipsis-start',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'ellipsis-end',
        totalPages,
    ];
};

const ProgrammeRecommendations = ({
    recommendedProgrammes,
    selectedProgrammes,
    selectedProgrammeCode = null,
    onAddProgramme,
    onRemoveProgramme,
    onSelectProgramme,
    classificationByCode = {},
    classificationLoadingCodes = new Set(),
    embedded = false,
    maxVisible = null,
    interactive = true,
    layout = 'cards',
    pageSize = JUPAS_RECOMMENDATION_VISIBLE_ROWS,
}) => {
    const { t, i18n } = useTranslation();
    const [expandedCodes, setExpandedCodes] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobileViewport, setIsMobileViewport] = useState(() => (
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 767px)').matches
            : false
    ));
    const [desktopPaginationSlots, setDesktopPaginationSlots] = useState(9);
    const paginationContainerRef = useRef(null);
    const [codeHoverTip, setCodeHoverTip] = useState({ visible: false, text: '', left: 0, top: 0, direction: 'above' });

    useEffect(() => {
        const media = window.matchMedia('(max-width: 767px)');
        const handleChange = () => {
            setIsMobileViewport(media.matches);
        };
        handleChange();
        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        const container = paginationContainerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return undefined;

        const updateLimit = () => {
            const width = container.getBoundingClientRect().width;
            const centreWidth = Math.max(0, width - 64);
            const totalItemSlots = Math.floor((centreWidth + 4) / 36);
            const nextSlots = Math.max(7, totalItemSlots);
            setDesktopPaginationSlots((current) => (
                current === nextSlots ? current : nextSlots
            ));
        };

        updateLimit();
        const observer = new ResizeObserver(updateLimit);
        observer.observe(container);
        return () => observer.disconnect();
    });

    const displayedProgrammes = recommendedProgrammes || [];
    const headProgrammeCode = displayedProgrammes[0]?.code || '';

    useEffect(() => {
        setCurrentPage(1);
    }, [headProgrammeCode, pageSize]);

    useEffect(() => {
        const total = displayedProgrammes.length;
        const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
        setCurrentPage((page) => Math.min(Math.max(1, page), pages));
    }, [displayedProgrammes.length, pageSize]);

    const toggleExpanded = (code) => {
        setExpandedCodes((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const showCodeHoverTip = (text, el) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const direction = rect.top < viewportHeight * 0.25 ? 'below' : 'above';
        setCodeHoverTip({
            visible: true,
            text,
            left: rect.left + rect.width / 2,
            top: direction === 'below' ? rect.bottom : rect.top,
            direction
        });
    };
    const hideCodeHoverTip = () => setCodeHoverTip({ visible: false, text: '', left: 0, top: 0, direction: 'above' });

    const getClassificationDisplay = (programme) => {
        const raw =
            classificationByCode[programme.code]?.classification
            ?? programme.classification
            ?? programme.classification_level;
        if (isClassificationUnavailable(raw)) {
            return getClassificationBadgeDisplay(raw, t);
        }
        const classification = Number(raw);
        if (!Number.isFinite(classification)) return null;
        return getClassificationBadgeDisplay(classification, t);
    };

    const calculateAdmissionRate = (programme) => {
        if (!programme.stats_apply?.[0] || !programme.stats_offer?.[0]) {
            return null;
        }

        const bandAApplicants = programme.stats_apply[0][1] || 0;
        const bandAOffers = programme.stats_offer[0][1] || 0;

        if (bandAApplicants === 0) {
            return null;
        }

        return ((bandAOffers / bandAApplicants) * 100).toFixed(1);
    };

    const getProgrammeBadgeDisplays = (programme) => {
        const classificationLoading = classificationLoadingCodes.has(programme.code);
        const classificationEntry = classificationByCode[programme.code];
        const resolvedClassificationDisplay = getClassificationDisplay(programme);
        const classificationDisplay = classificationLoading
            ? getClassificationLoadingBadgeDisplay(t)
            : resolvedClassificationDisplay;
        const fulfillmentDisplay = resolveRequirementFulfillmentDisplay(
            resolveRequirementBadgeValue(classificationEntry),
            t,
            {
                loading: classificationLoading,
                awaiting: isRequirementFulfillmentAwaiting(classificationEntry, { classificationLoading }),
            }
        );

        return { classificationDisplay, fulfillmentDisplay };
    };

    const getInterviewInfo = (programme) => {
        if (!programme.interview || programme.interview === 'No') {
            return null;
        }

        if (programme.interview === 'Yes (on a selective basis)') {
            return {
                mainPart: 'Yes',
                details: t('jupa.interviewSelectiveBasis'),
                showTooltip: true
            };
        } else if (programme.interview === 'May require interview and/or test') {
            return {
                mainPart: 'May require',
                details: t('jupa.interviewMayRequire'),
                showTooltip: true
            };
        }

        const match = programme.interview.match(/^([^(]+)(?:\(([^)]+)\))?$/);
        if (match) {
            const mainPart = match[1].trim();
            const details = match[2] ? match[2].trim() : null;
            return { mainPart, details, showTooltip: !!details };
        }

        return { mainPart: programme.interview, details: null, showTooltip: false };
    };

    const renderAdmissionStatsColumn = (programme, admissionRate, sectionBorderClass) => {
        const borderClass = sectionBorderClass;
        const hasAny = programme.intake || admissionRate || programme.stats_apply?.[0] || programme.stats_offer?.[0];
        if (!hasAny) return null;
        return (
            <>
                {programme.stats_apply?.[0] && (
                    <div className={`border-b ${borderClass} pb-1 mb-1`}>
                        <div className="font-semibold text-ink">{t('jupa.applications2024')}</div>
                        <div className="text-ink">{t('jupa.bandAtt', { num: programme.stats_apply[0][1] || 0 })}</div>
                        <div className="text-ink">{t('jupa.bandBtt', { num: programme.stats_apply[0][2] || 0 })}</div>
                        <div className="text-ink">{t('jupa.total', { num: programme.stats_apply[0][6] || 0 })}</div>
                    </div>
                )}
                {programme.stats_offer?.[0] && (
                    <div className={`border-b ${borderClass} pb-1 mb-1`}>
                        <div className="font-semibold text-ink">{t('jupa.offers2024')}</div>
                        <div className="text-ink">{t('jupa.bandAtt', { num: `${programme.stats_offer[0][1] || 0} (${((programme.stats_offer[0][1] || 0) / (programme.stats_apply?.[0]?.[1] || 1) * 100).toFixed(1)}%)` })}</div>
                        <div className="text-ink">{t('jupa.bandBtt', { num: `${programme.stats_offer[0][2] || 0} (${((programme.stats_offer[0][2] || 0) / (programme.stats_apply?.[0]?.[2] || 1) * 100).toFixed(1)}%)` })}</div>
                        <div className="text-ink">{t('jupa.total', { num: programme.stats_offer[0][6] || 0 })}</div>
                    </div>
                )}
                {admissionRate && (
                    <div className={`border-b ${borderClass} pb-1 mb-1`}>
                        <div className="font-semibold text-ink">{t('jupa.admissionRate')}</div>
                        <div className="text-ink">{admissionRate}%</div>
                    </div>
                )}
                {programme.intake && (
                    <div>
                        <div className="font-semibold text-ink">{t('jupa.intake')}</div>
                        <div className="text-ink">{programme.intake}</div>
                    </div>
                )}
            </>
        );
    };

    if (displayedProgrammes.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-ink-muted">{t('jupa.noProgrammeRecommendationsFound')}</p>
                <p className="text-sm text-ink-subtle mt-2">{t('jupa.tryImprovingScores')}</p>
            </div>
        );
    }

    const shouldPaginate = !embedded && maxVisible == null;
    const totalCount = displayedProgrammes.length;
    const totalPages = shouldPaginate ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
    const safePage = Math.min(Math.max(1, currentPage), totalPages);
    const visibleProgrammes = maxVisible
        ? displayedProgrammes.slice(0, maxVisible)
        : shouldPaginate
            ? displayedProgrammes.slice((safePage - 1) * pageSize, safePage * pageSize)
            : displayedProgrammes;
    const showPagination = shouldPaginate && totalPages > 1;
    const pageItems = showPagination
        ? (
            isMobileViewport
                ? buildMobilePageItems(safePage, totalPages)
                : buildPageItemsToFit(safePage, totalPages, desktopPaginationSlots)
        )
        : [];

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || page === safePage) return;
        setCurrentPage(page);
    };

    const handlePaginationKeyDown = (event) => {
        if (!showPagination) return;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToPage(safePage - 1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToPage(safePage + 1);
        }
    };

    const renderPaginationBar = () => {
        if (!showPagination) return null;

        const pageButtonClass = (active) => (
            `inline-flex h-6 min-w-5 shrink-0 items-center justify-center rounded px-0.5 text-[10px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:h-8 md:min-w-8 md:rounded-md md:px-2 md:text-sm ${
                active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-ink hover:bg-surface-muted'
            }`
        );

        return (
            <nav
                className="mt-3 flex flex-col items-center gap-2"
                aria-label={t('jupa.pageOf', { page: safePage, total: totalPages })}
            >
                <div
                    ref={paginationContainerRef}
                    className="grid w-full grid-cols-[1.75rem_minmax(0,1fr)_1.75rem] items-center md:grid-cols-[2rem_minmax(0,1fr)_2rem]"
                >
                    <button
                        type="button"
                        onClick={() => goToPage(safePage - 1)}
                        disabled={safePage <= 1}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-35 md:h-8 md:w-8"
                        aria-label={t('jupa.previousPage')}
                        title={t('jupa.previousPage')}
                    >
                        <FaChevronLeft className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
                    </button>
                    <div className="flex min-w-0 items-center justify-center gap-0.5 md:gap-1">
                        {pageItems.map((item) => (
                            typeof item === 'string' ? (
                                <span
                                    key={item}
                                    className="inline-flex h-6 w-2.5 shrink-0 items-center justify-center text-[10px] text-ink-subtle md:h-8 md:min-w-8 md:px-1 md:text-sm"
                                    aria-hidden
                                >
                                    …
                                </span>
                            ) : (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => goToPage(item)}
                                    className={pageButtonClass(item === safePage)}
                                    aria-label={t('jupa.goToPage', { page: item })}
                                    aria-current={item === safePage ? 'page' : undefined}
                                >
                                    {item}
                                </button>
                            )
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => goToPage(safePage + 1)}
                        disabled={safePage >= totalPages}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-35 md:h-8 md:w-8"
                        aria-label={t('jupa.nextPage')}
                        title={t('jupa.nextPage')}
                    >
                        <FaChevronRight className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
                    </button>
                </div>
                <p className="text-center text-xs text-ink-subtle">
                    {t('jupa.showingRecommendedProgrammes', {
                        showing: visibleProgrammes.length,
                        total: totalCount,
                    })}
                </p>
            </nav>
        );
    };

    if (embedded) {
        return (
            <div className="space-y-3">
                {visibleProgrammes.map((programme, index) => (
                    <JupasProgrammeCard
                        key={programme.code}
                        programme={programme}
                        rank={index + 1}
                        classification={classificationByCode[programme.code]?.classification ?? programme.classification}
                        classificationData={classificationByCode[programme.code]}
                        classificationLoading={classificationLoadingCodes.has(programme.code)}
                        defaultExpanded
                    />
                ))}
            </div>
        );
    }

    if (layout === 'compact-list') {
        return (
            <div
                className="outline-none"
                tabIndex={showPagination ? 0 : undefined}
                onKeyDown={handlePaginationKeyDown}
            >
                <div className={`h-[489px] overflow-hidden rounded-lg border border-edge bg-surface sm:h-[569px] ${JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS}`}>
                    {visibleProgrammes.map((programme) => {
                        const isSelected = selectedProgrammes.some(p => p.code === programme.code);
                        const isDetailSelected = selectedProgrammeCode === programme.code;
                        const { classificationDisplay, fulfillmentDisplay } = getProgrammeBadgeDisplays(programme);

                        const swipeRemoves = isSelected && Boolean(onRemoveProgramme);
                        const swipeEnabled = interactive && (swipeRemoves || Boolean(onAddProgramme));

                        return (
                            <MobileProgrammeSwipeAction
                                key={programme.code}
                                enabled={swipeEnabled}
                                tone={swipeRemoves ? 'remove' : 'add'}
                                actionLabel={swipeRemoves ? t('jupa.remove') : t('jupa.add')}
                                collapseOnAction={false}
                                onAction={() => (
                                    swipeRemoves
                                        ? onRemoveProgramme?.(programme)
                                        : onAddProgramme?.(programme)
                                )}
                                onTap={() => onSelectProgramme?.(programme)}
                                className="border-b border-edge last:border-b-0"
                            >
                                <div
                                    role={onSelectProgramme ? 'button' : undefined}
                                    tabIndex={onSelectProgramme ? 0 : undefined}
                                    onClick={() => onSelectProgramme?.(programme)}
                                    onKeyDown={(e) => {
                                        if (!onSelectProgramme) return;
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onSelectProgramme(programme);
                                        }
                                    }}
                                    className={`${JUPAS_PROGRAMME_BAR_ROW_CLASS} h-12 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 sm:h-14 ${
                                        isDetailSelected
                                            ? 'bg-primary/10 ring-1 ring-inset ring-primary/30'
                                            : 'bg-surface hover:bg-surface-muted'
                                    }`}
                                    aria-current={isDetailSelected ? 'true' : undefined}
                                >
                                    <div className={JUPAS_PROGRAMME_BAR_MAIN_CLASS}>
                                        <div className={JUPAS_PROGRAMME_BAR_IDENTITY_CLASS}>
                                            <span className={JUPAS_PROGRAMME_CODE_LABEL_CLASS}>
                                                <span className="truncate">{programme.code}</span>
                                            </span>
                                            <div className={JUPAS_PROGRAMME_BAR_TITLE_CLASS}>
                                                <p className="truncate text-sm font-medium text-ink">
                                                    {getProgrammePrimaryTitle(programme, i18n.language)}
                                                </p>
                                                <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs">
                                                    <span className="min-w-0 flex-1 truncate text-ink-subtle">
                                                        {getInstitutionShortLabel(programme.institution, i18n.language)}
                                                    </span>
                                                    {isSelected ? (
                                                        <span className="shrink-0 rounded-full bg-green-50 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-green-700 dark:bg-green-950/60 dark:text-green-300 md:hidden">
                                                            {t('jupa.workspace.added')}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={JUPAS_PROGRAMME_BAR_TRAILING_CLASS}>
                                        {fulfillmentDisplay ? (
                                            <span
                                                className={`inline-flex ${REQUIREMENT_BADGE_SIZE_CLASS} shrink-0 items-center justify-center rounded-full font-bold leading-none ${fulfillmentDisplay.className}`}
                                                title={fulfillmentDisplay.label}
                                                aria-label={fulfillmentDisplay.label}
                                                role={fulfillmentDisplay.isLoading ? 'status' : undefined}
                                            >
                                                {fulfillmentDisplay.isLoading ? (
                                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
                                                ) : (
                                                    fulfillmentDisplay.code
                                                )}
                                            </span>
                                        ) : null}
                                        {classificationDisplay ? (
                                            <span
                                                className={`inline-flex ${CLASSIFICATION_BADGE_WIDTH_CLASS} ${CLASSIFICATION_BADGE_TEXT_CLASS} shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded px-1 py-1.5 font-medium sm:px-1.5 ${classificationDisplay.className}`}
                                                role={classificationDisplay.isLoading ? 'status' : undefined}
                                                title={classificationDisplay.label}
                                            >
                                                <span className="truncate">{classificationDisplay.label}</span>
                                            </span>
                                        ) : null}
                                        {interactive ? (
                                            <div className="hidden md:block">
                                                {isSelected && onRemoveProgramme ? (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemoveProgramme(programme);
                                                        }}
                                                        className={`${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${JUPAS_HIGH_RISK_ACTION_CLASS}`}
                                                        aria-label={t('jupa.remove')}
                                                        title={t('jupa.remove')}
                                                    >
                                                        <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} group-hover:bg-red-50 group-active:bg-red-100`}>
                                                            <FaMinusCircle className="h-4 w-4" aria-hidden />
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAddProgramme(programme);
                                                        }}
                                                        className={`${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${JUPAS_VERY_SAFE_ACTION_CLASS}`}
                                                        aria-label={t('jupa.add')}
                                                        title={t('jupa.add')}
                                                    >
                                                        <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} group-hover:bg-green-50 group-active:bg-green-100`}>
                                                            <FaPlusCircle className="h-4 w-4" aria-hidden />
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </MobileProgrammeSwipeAction>
                        );
                    })}
                </div>
                {renderPaginationBar()}
            </div>
        );
    }

    return (
        <div
            className={embedded ? 'space-y-2' : 'space-y-4'}
            tabIndex={showPagination ? 0 : undefined}
            onKeyDown={handlePaginationKeyDown}
        >
            <div
                className={
                    embedded
                        ? 'grid grid-cols-1 gap-3 items-start'
                        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start'
                }
            >
                {visibleProgrammes.map((programme) => {
                    const isSelected = selectedProgrammes.some(p => p.code === programme.code);
                    const { classificationDisplay, fulfillmentDisplay } = getProgrammeBadgeDisplays(programme);
                    const admissionRate = calculateAdmissionRate(programme);
                    const isOpen = !!expandedCodes[programme.code];
                    const interviewInfo = getInterviewInfo(programme);
                    const dividerBorder = isSelected ? 'border-green-500' : 'border-primary';
                    const admissionCol = renderAdmissionStatsColumn(programme, admissionRate, dividerBorder);
                    const hasAdmissionCol = admissionCol != null;
                    const recDropdownHasContent = !!(programme.duration?.trim() || interviewInfo || hasAdmissionCol);
                    const abbrClass = 'text-xs text-ink bg-surface-muted px-2 py-1 rounded cursor-pointer transition-colors hover:bg-red-50 hover:text-red-700 border border-transparent hover:border-red-200';
                    const hasLeftCol = !!(programme.duration?.trim() || interviewInfo);
                    const twoCol = hasLeftCol && hasAdmissionCol;

                    return (
                        <div
                            key={programme.code}
                            className={`border rounded-lg transition-all duration-200 flex flex-col ${embedded ? 'p-3' : 'p-4'} ${isSelected
                                ? 'border-green-500 bg-green-50 hover:bg-green-100'
                                : 'border-edge bg-surface hover:border-link/40 hover:shadow-md'
                                }`}
                        >
                            <div className="flex justify-between items-start gap-2 mb-2">
                                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                                    {programme.short_name ? (
                                        <span
                                            className={abbrClass}
                                            role={programme.url ? 'link' : undefined}
                                            tabIndex={programme.url ? 0 : undefined}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (programme.url) {
                                                    window.open(programme.url, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                            onMouseEnter={(e) => programme.url && showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                            onMouseLeave={hideCodeHoverTip}
                                            onFocus={(e) => programme.url && showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                            onBlur={hideCodeHoverTip}
                                        >
                                            {programme.short_name}
                                        </span>
                                    ) : programme.url ? (
                                        <span
                                            className={abbrClass}
                                            role="link"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(programme.url, '_blank', 'noopener,noreferrer');
                                            }}
                                            onMouseEnter={(e) => showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                            onMouseLeave={hideCodeHoverTip}
                                            onFocus={(e) => showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                            onBlur={hideCodeHoverTip}
                                        >
                                            {programme.code}
                                        </span>
                                    ) : (
                                        <span className="text-xs font-semibold text-link">{programme.code}</span>
                                    )}
                                    {recDropdownHasContent && (
                                        <button
                                            type="button"
                                            className={`p-1 rounded focus:outline-none focus:ring-2 ${isSelected
                                                ? 'text-green-600 hover:bg-green-100 hover:text-green-800 focus:ring-green-300'
                                                : 'text-link hover:bg-blue-100 hover:text-link-hover focus:ring-blue-300'
                                                }`}
                                            aria-expanded={isOpen}
                                            aria-label={isOpen ? t('jupa.collapseProgrammeDetails') : t('jupa.expandProgrammeDetails')}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpanded(programme.code);
                                            }}
                                        >
                                            <svg className={`w-5 h-5 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    {fulfillmentDisplay && (
                                        <span
                                            className={`inline-flex ${REQUIREMENT_BADGE_SIZE_CLASS} shrink-0 items-center justify-center rounded-full font-bold leading-none ${fulfillmentDisplay.className}`}
                                            title={fulfillmentDisplay.label}
                                            aria-label={fulfillmentDisplay.label}
                                            role={fulfillmentDisplay.isLoading ? 'status' : undefined}
                                        >
                                            {fulfillmentDisplay.isLoading ? (
                                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
                                            ) : fulfillmentDisplay.code}
                                        </span>
                                    )}
                                    {classificationDisplay && (
                                        <span
                                            className={`inline-flex ${CLASSIFICATION_BADGE_WIDTH_CLASS} ${CLASSIFICATION_BADGE_TEXT_CLASS} shrink-0 items-center justify-center gap-1 rounded px-1 py-1.5 font-medium sm:px-1.5 ${classificationDisplay.className}`}
                                            role={classificationDisplay.isLoading ? 'status' : undefined}
                                        >
                                            <span className="truncate">{classificationDisplay.label}</span>
                                        </span>
                                    )}
                                    {isSelected && (
                                        <span className="text-green-600 text-sm font-medium shrink-0">
                                            ✓ {t('common.selected')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2 text-sm mb-2">
                                <div
                                    className={`font-bold text-link text-lg w-fit ${programme.url ? 'cursor-pointer hover:underline' : ''}`}
                                    role={programme.url ? 'link' : undefined}
                                    tabIndex={programme.url ? 0 : undefined}
                                    onClick={(e) => {
                                        if (programme.url) {
                                            e.stopPropagation();
                                            window.open(programme.url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    onMouseEnter={(e) => programme.url && showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                    onMouseLeave={hideCodeHoverTip}
                                    onFocus={(e) => programme.url && showCodeHoverTip(t('common.clickToView'), e.currentTarget)}
                                    onBlur={hideCodeHoverTip}
                                >
                                    {programme.code}
                                </div>
                                <p className="text-sm text-ink break-words">
                                    {getProgrammePrimaryTitle(programme, i18n.language)}
                                </p>
                                {getProgrammeSecondaryTitle(programme, i18n.language) && (
                                    <p className="text-xs text-ink-muted break-words">
                                        {getProgrammeSecondaryTitle(programme, i18n.language)}
                                    </p>
                                )}
                                <p className="text-xs text-ink-subtle pb-3">
                                    {getInstitutionShortLabel(programme.institution, i18n.language)}
                                </p>
                            </div>

                            {recDropdownHasContent && (
                                <CollapsibleSlide open={isOpen}>
                                        <div className="pt-3 pb-4 text-sm mb-3">
                                            <div className={`grid gap-4 items-start ${twoCol ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                                {hasLeftCol && (
                                                    <div className="space-y-2 min-w-0">
                                                        {programme.duration?.trim() && (
                                                            <div className="text-ink text-sm">
                                                                <span className="text-ink-muted">{t('jupa.durationBadgeTooltip')}: </span>
                                                                {programme.duration.trim()}
                                                            </div>
                                                        )}
                                                        {interviewInfo && (
                                                            <div className="text-ink text-sm">
                                                                <div>{t('jupa.interview')}: {interviewInfo.mainPart}</div>
                                                                {interviewInfo.details && (
                                                                    <div className="text-xs text-ink-muted mt-0.5">{interviewInfo.details}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {hasAdmissionCol && (
                                                    <div className={`min-w-0 ${twoCol ? `border-t md:border-t-0 md:border-l ${dividerBorder} pt-3 md:pt-0 md:pl-4` : `border-t ${dividerBorder} pt-3`}`}>
                                                        <div className="font-semibold text-ink mb-2">{t('jupa.admissionStatistics')}</div>
                                                        <div className="space-y-1 text-xs">
                                                            {admissionCol}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                </CollapsibleSlide>
                            )}

                            {interactive && (
                                <button
                                    type="button"
                                    disabled={isSelected}
                                    onClick={() => onAddProgramme(programme)}
                                    className="w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle disabled:hover:bg-surface-muted"
                                >
                                    {isSelected ? t('jupa.selected') : t('jupa.add')}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {renderPaginationBar()}
            {codeHoverTip.visible && createPortal(
                <div
                    className="fixed z-[9999] max-w-xs whitespace-pre-wrap break-words rounded bg-zinc-900 px-3 py-2 text-xs text-white shadow-lg pointer-events-none"
                    style={{
                        left: codeHoverTip.left,
                        top: codeHoverTip.direction === 'below' ? codeHoverTip.top + 8 : codeHoverTip.top - 8,
                        transform: codeHoverTip.direction === 'below' ? 'translateX(-50%)' : 'translate(-50%, -100%)',
                        opacity: 1
                    }}
                >
                    {codeHoverTip.text}
                </div>,
                document.body
            )}
        </div>
    );
};

export default ProgrammeRecommendations;
